import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext"; // 언어 Context 사용
import { authAPI } from "../../services/api";
import {
  FiPlus,
  FiUsers,
  FiFolder,
  FiClock,
  FiStar,
  FiArchive,
} from "react-icons/fi";
import SideBar from "./SideBar";
import "./Dashboard.css";

// 다국어 지원을 위한 번역 객체
const translations = {
  ko: {
    createProject: "새 프로젝트",
    joinProject: "프로젝트 참여하기",
    createNewProject: "새 프로젝트 생성",
    recentProjects: "최근 사용한 프로젝트",
    ownerProjects: "내가 운영중인 프로젝트",
    memberProjects: "내가 참여하는 프로젝트",
    ecrfProjects: "EDC 프로젝트",
    ktasProjects: "KTAS 프로젝트",
    noDescription: "설명 없음",
    open: "열기",
    loading: "프로젝트 정보를 불러오는 중...",
    userInfoError: "사용자 정보를 가져오는데 실패했습니다.",
    authError: "인증 정보를 가져오는데 실패했습니다.",
    inPreparation: "준비 중입니다.",
  },
  en: {
    createProject: "Create New Project",
    joinProject: "Join Project",
    createNewProject: "Create New Project",
    recentProjects: "Recent Projects",
    ownerProjects: "Projects I Own",
    memberProjects: "Projects I Participate In",
    ecrfProjects: "eCRF Projects",
    ktasProjects: "KTAS Projects",
    noDescription: "No description",
    open: "Open",
    loading: "Loading projects...",
    userInfoError: "Failed to fetch user information.",
    authError: "Failed to fetch authentication information.",
    inPreparation: "In preparation.",
  },
  ja: {
    createProject: "新しいプロジェクトを作成",
    joinProject: "プロジェクトに参加",
    createNewProject: "新規プロジェクト",
    recentProjects: "最近使用したプロジェクト",
    ownerProjects: "所有プロジェクト",
    memberProjects: "参加しているプロジェクト",
    ecrfProjects: "eCRFプロジェクト",
    ktasProjects: "KTASプロジェクト",
    noDescription: "説明なし",
    open: "開く",
    loading: "プロジェクト情報を読み込んでいます...",
    userInfoError: "ユーザー情報の取得に失敗しました。",
    authError: "認証情報の取得に失敗しました。",
    inPreparation: "準備中です。",
  },
  "zh-CN": {
    createProject: "创建新项目",
    joinProject: "加入项目",
    createNewProject: "创建新项目",
    recentProjects: "最近使用的项目",
    ownerProjects: "我拥有的项目",
    memberProjects: "我参与的项目",
    ecrfProjects: "eCRF项目",
    ktasProjects: "KTAS项目",
    noDescription: "无描述",
    open: "打开",
    loading: "正在加载项目信息...",
    userInfoError: "获取用户信息失败。",
    authError: "获取身份验证信息失败。",
    inPreparation: "准备中。",
  },
  "zh-TW": {
    createProject: "創建新項目",
    joinProject: "加入項目",
    createNewProject: "創建新項目",
    recentProjects: "最近使用的項目",
    ownerProjects: "我擁有的項目",
    memberProjects: "我參與的項目",
    ecrfProjects: "eCRF項目",
    ktasProjects: "KTAS項目",
    noDescription: "無描述",
    open: "打開",
    loading: "正在加載項目信息...",
    userInfoError: "獲取用戶信息失敗。",
    authError: "獲取身份驗證信息失敗。",
    inPreparation: "準備中。",
  },
  it: {
    createProject: "Crea Nuovo Progetto",
    joinProject: "Partecipa al Progetto",
    createNewProject: "Crea Nuovo Progetto",
    recentProjects: "Progetti Recenti",
    ownerProjects: "Progetti di cui Sono Proprietario",
    memberProjects: "Progetti a cui Partecipo",
    ecrfProjects: "Progetti eCRF",
    ktasProjects: "Progetti KTAS",
    noDescription: "Nessuna descrizione",
    open: "Apri",
    loading: "Caricamento dei progetti...",
    userInfoError: "Impossibile recuperare le informazioni dell'utente.",
    authError: "Impossibile recuperare le informazioni di autenticazione.",
    inPreparation: "In preparazione.",
  },
  fr: {
    createProject: "Créer un Nouveau Projet",
    joinProject: "Rejoindre un Projet",
    createNewProject: "Créer un Nouveau Projet",
    recentProjects: "Projets Récents",
    ownerProjects: "Mes Projets",
    memberProjects: "Projets auxquels je Participe",
    ecrfProjects: "Projets eCRF",
    ktasProjects: "Projets KTAS",
    noDescription: "Pas de description",
    open: "Ouvrir",
    loading: "Chargement des projets...",
    userInfoError: "Échec de la récupération des informations utilisateur.",
    authError: "Échec de la récupération des informations d'authentification.",
    inPreparation: "En préparation.",
  },
  de: {
    createProject: "Neues Projekt erstellen",
    joinProject: "Projekt beitreten",
    createNewProject: "Neues Projekt erstellen",
    recentProjects: "Kürzlich verwendete Projekte",
    ownerProjects: "Meine eigenen Projekte",
    memberProjects: "Projekte, an denen ich teilnehme",
    ecrfProjects: "eCRF-Projekte",
    ktasProjects: "KTAS-Projekte",
    noDescription: "Keine Beschreibung",
    open: "Öffnen",
    loading: "Projekte werden geladen...",
    userInfoError: "Benutzerinformationen konnten nicht abgerufen werden.",
    authError:
      "Authentifizierungsinformationen konnten nicht abgerufen werden.",
    inPreparation: "In Vorbereitung.",
  },
  es: {
    createProject: "Crear Nuevo Proyecto",
    joinProject: "Unirse a un Proyecto",
    createNewProject: "Crear Nuevo Proyecto",
    recentProjects: "Proyectos Recientes",
    ownerProjects: "Proyectos que Administro",
    memberProjects: "Proyectos en los que Participo",
    ecrfProjects: "Proyectos eCRF",
    ktasProjects: "Proyectos KTAS",
    noDescription: "Sin descripción",
    open: "Abrir",
    loading: "Cargando información de proyectos...",
    userInfoError: "Error al obtener la información del usuario.",
    authError: "Error al obtener la información de autenticación.",
    inPreparation: "En preparación.",
  },
  ru: {
    createProject: "Создать новый проект",
    joinProject: "Присоединиться к проекту",
    createNewProject: "Создать новый проект",
    recentProjects: "Недавние проекты",
    ownerProjects: "Мои проекты",
    memberProjects: "Проекты, в которых я участвую",
    ecrfProjects: "Проекты eCRF",
    ktasProjects: "Проекты KTAS",
    noDescription: "Без описания",
    open: "Открыть",
    loading: "Загрузка информации о проектах...",
    userInfoError: "Не удалось получить информацию о пользователе.",
    authError: "Не удалось получить информацию об аутентификации.",
    inPreparation: "В процессе подготовки.",
  },
  pt: {
    createProject: "Criar Novo Projeto",
    joinProject: "Participar de um Projeto",
    createNewProject: "Criar Novo Projeto",
    recentProjects: "Projetos Recentes",
    ownerProjects: "Projetos que eu Administro",
    memberProjects: "Projetos em que Participo",
    ecrfProjects: "Projetos eCRF",
    ktasProjects: "Projetos KTAS",
    noDescription: "Sem descrição",
    open: "Abrir",
    loading: "Carregando informações de projetos...",
    userInfoError: "Falha ao obter informações do usuário.",
    authError: "Falha ao obter informações de autenticação.",
    inPreparation: "Em preparação.",
  },
  ar: {
    createProject: "إنشاء مشروع جديد",
    joinProject: "الانضمام إلى مشروع",
    createNewProject: "إنشاء مشروع جديد",
    recentProjects: "المشاريع الأخيرة",
    ownerProjects: "المشاريع التي أديرها",
    memberProjects: "المشاريع التي أشارك فيها",
    ecrfProjects: "مشاريع eCRF",
    ktasProjects: "مشاريع KTAS",
    noDescription: "بدون وصف",
    open: "فتح",
    loading: "جاري تحميل معلومات المشاريع...",
    userInfoError: "فشل في الحصول على معلومات المستخدم.",
    authError: "فشل في الحصول على معلومات المصادقة.",
    inPreparation: "قيد التحضير.",
  },
  hi: {
    createProject: "नया प्रोजेक्ट बनाएं",
    joinProject: "प्रोजेक्ट में शामिल हों",
    createNewProject: "नया प्रोजेक्ट बनाएं",
    recentProjects: "हाल के प्रोजेक्ट",
    ownerProjects: "मेरे द्वारा संचालित प्रोजेक्ट",
    memberProjects: "जिन प्रोजेक्ट में मैं शामिल हूं",
    ecrfProjects: "eCRF प्रोजेक्ट",
    ktasProjects: "KTAS प्रोजेक्ट",
    noDescription: "कोई विवरण नहीं",
    open: "खोलें",
    loading: "प्रोजेक्ट जानकारी लोड हो रही है...",
    userInfoError: "उपयोगकर्ता जानकारी प्राप्त करने में विफल।",
    authError: "प्रमाणीकरण जानकारी प्राप्त करने में विफल।",
    inPreparation: "तैयारी में है।",
  },
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  // 언어 Context 사용
  const { currentLanguage } = useLanguage();

  const [userInfo, setUserInfo] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // 프로젝트 정보를 담을 상태 변수들
  const [recentProjects, setRecentProjects] = useState([]);
  const [ownerProjects, setOwnerProjects] = useState([]);
  const [memberProjects, setMemberProjects] = useState([]);
  const [ecrfProjects, setEcrfProjects] = useState([]);
  const [ktasProjects, setKtasProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // 구독 정보 상태
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);

  // 현재 언어에 따른 텍스트 가져오기
  const getText = (key) => {
    return translations[currentLanguage]?.[key] || translations["en"][key];
  };

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await authAPI.getUserInfo();
        //console.log("auth/me API 응답:", response);

        if (response && response.success) {
          setUserInfo(response.user);
          // 구독 정보 저장
          if (response.subscription) {
            setSubscriptionInfo(response.subscription);
          }

          // 프로젝트 정보가 있는 경우 상태 업데이트
          if (response.projects) {
            console.log("프로젝트 정보:", response.projects);
            setRecentProjects(response.projects.recentProjects || []);
            setOwnerProjects(response.projects.ownerProjects || []);
            setMemberProjects(response.projects.memberProjects || []);
            setEcrfProjects(response.projects.ecrfProjects || []);
            setKtasProjects(response.projects.ktasProjects || []);
          } else {
            // console.log("프로젝트 정보가 없습니다.");
            // 프로젝트 정보가 없는 경우 빈 배열로 초기화
            setRecentProjects([]);
            setOwnerProjects([]);
            setMemberProjects([]);
            setEcrfProjects([]);
            setKtasProjects([]);
          }
        } else {
          console.error(
            "사용자 정보가 없거나 형식이 올바르지 않습니다:",
            response
          );
          setError(getText("userInfoError"));
        }
      } catch (error) {
        //console.error("auth/me API 호출 실패:", error);
        setError(getText("authError"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserInfo();
  }, [currentLanguage]); // 언어 변경시 에러 메시지도 업데이트되도록

  // 새 프로젝트 생성 페이지로 이동
  const handleCreateProject = () => {
    navigate("/create-project");
  };

  // 프로젝트 참여 페이지로 이동
  const handleJoinProject = () => {
    navigate("/join-project");
  };

  // 프로젝트 열기 처리 함수
  const handleOpenProject = (project) => {
    sessionStorage.setItem("currentProject", JSON.stringify(project));

    switch (project.project_type) {
      case "eCRF":
        navigate(`/ecrf`);
        break;
      case "EDC":
        navigate(`/ecrf`);
        break;
      case "KTAS":
        navigate(`/ktas`);
        break;
      case "Note":
        navigate(`/note`);
        break;
      default:
        navigate(`/project-view`);
    }
  };

  // 프로젝트 카드 컴포넌트
  const ProjectCard = ({ project }) => (
    <div className="project-card">
      <h3>{project.name}</h3>
      <p className="project-description">
        {project.description || getText("noDescription")}
      </p>
      <div className="project-card-footer">
        <div className="project-meta">
          <span className="project-type">{project.project_type}</span>
          {project.role_name && (
            <span className="project-role">{project.role_name}</span>
          )}
        </div>
        <button onClick={() => handleOpenProject(project)}>
          {getText("open")}
        </button>
      </div>
    </div>
  );

  // 빈 프로젝트 카드 (새 프로젝트 생성용)
  const EmptyProjectCard = () => (
    <div className="project-card empty-card" onClick={handleCreateProject}>
      <FiPlus className="plus-icon" />
      <p>{getText("createNewProject")}</p>
    </div>
  );

  // 프로젝트 섹션 컴포넌트
  const ProjectSection = ({ titleKey, icon, projects }) => (
    <div className="project-section">
      <div className="section-header">
        <div
          style={{ display: "flex", alignItems: "center", marginRight: "auto" }}
        >
          <span className="section-icon">{icon}</span>
          <h2>{getText(titleKey)}</h2>
        </div>
      </div>
      <div className="project-grid">
        {isLoading ? (
          <div className="loading-message">{getText("loading")}</div>
        ) : projects && projects.length > 0 ? (
          projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))
        ) : (
          <EmptyProjectCard />
        )}
      </div>
    </div>
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="dashboard-layout">
      <SideBar
        userInfo={userInfo}
        subscriptionInfo={subscriptionInfo}
        collapsed={sidebarCollapsed}
        toggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <div
        className={`dashboard-container ${
          sidebarCollapsed ? "sidebar-collapsed" : ""
        }`}
      >
        <header className="dashboard-header">
          <div className="action-buttons-row">
            {/* <button
              className="create-project-button"
              onClick={handleCreateProject}
            >
              <FiPlus className="action-icon" />
              <span>{getText("createProject")}</span>
            </button> */}
            <button className="join-project-button" onClick={handleJoinProject}>
              <FiUsers className="action-icon" />
              <span>{getText("joinProject")}</span>
            </button>
          </div>
        </header>

        {error && <div className="error-message">{error}</div>}

        <div className="dashboard-content">
          <ProjectSection
            titleKey="recentProjects"
            icon={<FiClock className="section-icon" />}
            projects={recentProjects}
          />
          <ProjectSection
            titleKey="ownerProjects"
            icon={<FiStar className="section-icon" />}
            projects={ownerProjects}
          />
          <ProjectSection
            titleKey="memberProjects"
            icon={<FiUsers className="section-icon" />}
            projects={memberProjects}
          />
          <ProjectSection
            titleKey="ecrfProjects"
            icon={<FiFolder className="section-icon" />}
            projects={ecrfProjects}
          />
          {/*
          <ProjectSection
            titleKey="ktasProjects"
            icon={<FiFolder className="section-icon" />}
            projects={ktasProjects}
          />
          */}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
